import type { ReactNode } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Control, FieldErrors } from 'react-hook-form'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  FieldRequiredIndicator,
  FieldRoot,
  Grid,
  Stack,
  Text,
} from '@enechain/ecloud-designsystem'

import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { convertAreaLabel } from '~/models/area'
import type { CivilDate } from '~/models/civilDate'
import { plantPowerTypeLabel } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/models/labels'
import { AffiliationSection } from '~/pages/AdminPlantUnitDetail/components/AffiliationSection'
import {
  DateField,
  NoteField,
  TextField,
} from '~/pages/AdminPlantUnitMaster/components/PlantUnitFormFields'
import { SiteSelectField } from '~/pages/AdminPlantUnitMaster/components/SiteSelectField'
import { formatCivilSlash } from '~/pages/AdminPlantUnitMaster/models/affiliation'
import { generatorCategoryLabel } from '~/pages/AdminPlantUnitMaster/models/labels'
import { plantCandidates } from '~/pages/AdminPlantUnitMaster/models/unitCandidates'
import {
  GENERATOR_IDENTIFIER_MAX_LENGTH,
  POWER_SOURCE_ID_MAX_LENGTH,
  type PlantUnitFormInput,
  UNIT_NAME_ABBR_MAX_LENGTH,
  UNIT_NAME_MAX_LENGTH,
  UNIT_NOTE_MAX_LENGTH,
} from '~/pages/AdminPlantUnitMaster/models/unitForm'
import {
  GENERATOR_CATEGORY_OPTIONS,
  UNIT_POWER_TYPE_OPTIONS,
} from '~/pages/AdminPlantUnitMaster/models/unitRows'
import type { PlantUnitRow } from '~/pages/AdminPlantUnitMaster/models/unitRows'

const NO_VALUE = '—'

const Item = ({
  label,
  value,
  full = false,
  editor,
  htmlFor,
  required = false,
}: {
  label: string
  value: ReactNode
  editor?: ReactNode
  htmlFor?: string
  required?: boolean
  /** 備考: 行の残り全部を使い、改行を保つ。 */
  full?: boolean
}): React.ReactElement => (
  <FieldRoot display="contents" required={required}>
    <Text
      as="dt"
      textStyle="label"
      alignSelf={full ? 'start' : undefined}
      textAlign="right"
      whiteSpace="nowrap"
    >
      {editor !== undefined ? (
        <FieldLabel htmlFor={htmlFor}>
          {label}
          {required && <FieldRequiredIndicator />}
        </FieldLabel>
      ) : (
        label
      )}
    </Text>
    <Box
      as="dd"
      minWidth={0}
      margin={0}
      css={full ? { gridColumn: '2 / -1' } : undefined}
    >
      {editor ??
        (typeof value !== 'string' && typeof value !== 'number' ? (
          value
        ) : (
          <Text
            as="span"
            textStyle="body"
            whiteSpace={full ? 'pre-wrap' : 'normal'}
            overflowWrap="anywhere"
          >
            {value === '' ? NO_VALUE : value}
          </Text>
        ))}
    </Box>
  </FieldRoot>
)

type Props = {
  unit: PlantUnitRow
  /** 発電所の名称。引けなければ undefined。 */
  siteName: string | undefined
  /** 系統コード (コードと名称)。未設定・引けなければ undefined。 */
  plantLabel: string | undefined
  editing?: {
    control: Control<PlantUnitFormInput>
    errors: FieldErrors<PlantUnitFormInput>
    disabled: boolean
    sites: ReadonlyArray<{ id: string; siteName: string; areaCode: AreaCode }>
    plants: ReadonlyArray<{
      id: string
      plantCode: string
      plantName: string
      areaCode: AreaCode
    }>
    onSiteChanged: (siteId: string | undefined) => void
  }
}

const dateText = (d: CivilDate | undefined): string =>
  d === undefined ? '' : formatCivilSlash(d)

/**
 * 電源ユニット詳細の基本情報。閲覧・編集で同じ項目を 3 組 / 行の定義リストに並べる。
 * エリアは系統コード (無ければ発電所) のエリアで、入力欄にしない。
 */
export const UnitBasicView = ({
  unit,
  siteName,
  plantLabel,
  editing,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const f = (key: string): string =>
    t(`page:adminPlantUnitMaster.unit.field.${key}`)
  const fieldEditor = (name: keyof PlantUnitFormInput): ReactNode =>
    editing === undefined ? undefined : (
      <DetailEditor editing={editing} name={name} />
    )
  const item = (
    name: keyof PlantUnitFormInput,
    value: ReactNode,
    required = false,
    full = false,
  ): React.ReactElement => (
    <Item
      editor={fieldEditor(name)}
      full={full}
      htmlFor={`detail-unit-${name}`}
      label={f(
        name === 'siteId'
          ? 'site'
          : name === 'plantMasterId'
            ? 'plantCode'
            : name,
      )}
      required={required}
      value={value}
    />
  )
  return (
    <Stack gap={1}>
      <Group>
        {item(
          'generatorCategory',
          generatorCategoryLabel(t, unit.generatorCategory),
          true,
        )}
        {item('powerType', plantPowerTypeLabel(unit.powerType), true)}
        <Item
          label={f('areaCode')}
          value={
            editing === undefined ? (
              convertAreaLabel(unit.areaCode)
            ) : (
              <DraftArea editing={editing} />
            )
          }
        />
        {item(
          'authorizedOutputKw',
          unit.authorizedOutputKw.toLocaleString('ja-JP'),
          true,
        )}
      </Group>
      <Group>
        {item('siteId', siteName ?? '', true)}
        {item('unitName', unit.unitName, true)}
        {item('unitNameAbbr', unit.unitNameAbbr, true)}
      </Group>
      <Group>
        {item('plantMasterId', plantLabel ?? '')}
        <Item
          label={f('affiliationBg')}
          value={
            <AffiliationSection
              affiliations={unit.affiliations}
              hasPlantMaster={unit.plantMasterId !== undefined}
            />
          }
        />
      </Group>
      <Group>
        {item('operationStartDate', dateText(unit.operationStartDate), true)}
        {item('operationEndDate', dateText(unit.operationEndDate))}
        {item('validFrom', dateText(unit.validFrom), true)}
        {item('validTo', dateText(unit.validTo))}
      </Group>
      <Group>
        {item('receivingPointId', unit.receivingPointId)}
        {item('powerSourceId', unit.powerSourceId)}
        {item('generatorIdentifier', unit.generatorIdentifier)}
      </Group>
      <Group>{item('note', unit.note, false, true)}</Group>
    </Stack>
  )
}

/** 編集中のエリア表示は、現在選んだ系統コードを優先し、無ければ発電所から引く。 */
const DraftArea = ({
  editing,
}: {
  editing: NonNullable<Props['editing']>
}): React.ReactElement => {
  const plantMasterId = useWatch({
    control: editing.control,
    name: 'plantMasterId',
  })
  const siteId = useWatch({ control: editing.control, name: 'siteId' })
  const areaCode =
    editing.plants.find((plant) => plant.id === plantMasterId)?.areaCode ??
    editing.sites.find((site) => site.id === siteId)?.areaCode ??
    AreaCode.UNSPECIFIED
  return <>{convertAreaLabel(areaCode)}</>
}

const Group = ({ children }: { children: ReactNode }): React.ReactElement => (
  <Grid
    as="dl"
    alignItems="center"
    gridRowGap={1}
    gridColumnGap={4}
    margin={0}
    css={{
      gridTemplateColumns: 'repeat(3, 10rem minmax(0, 1fr))',
      '@media (max-width: 1199.98px)': {
        gridTemplateColumns: 'repeat(2, 10rem minmax(0, 1fr))',
      },
    }}
  >
    {children}
  </Grid>
)

const MAX_LENGTHS: Partial<Record<keyof PlantUnitFormInput, number>> = {
  unitName: UNIT_NAME_MAX_LENGTH,
  unitNameAbbr: UNIT_NAME_ABBR_MAX_LENGTH,
  powerSourceId: POWER_SOURCE_ID_MAX_LENGTH,
  generatorIdentifier: GENERATOR_IDENTIFIER_MAX_LENGTH,
  note: UNIT_NOTE_MAX_LENGTH,
}

const DetailEditor = ({
  name,
  editing,
}: {
  name: keyof PlantUnitFormInput
  editing: NonNullable<Props['editing']>
}): React.ReactElement => {
  const { t } = useTranslation()
  const { control, errors, disabled, sites, plants, onSiteChanged } = editing
  const siteId = useWatch({ control, name: 'siteId' })
  const plantMasterId = useWatch({ control, name: 'plantMasterId' })
  const f = (key: string): string =>
    t(`page:adminPlantUnitMaster.unit.field.${key}`)
  const id = `detail-unit-${name}`
  if (
    name === 'operationStartDate' ||
    name === 'operationEndDate' ||
    name === 'validFrom' ||
    name === 'validTo'
  ) {
    return (
      <DateField
        control={control}
        disabled={disabled}
        errors={errors}
        hideLabel
        hint={
          name === 'operationEndDate'
            ? f('operationEndDateHint')
            : name === 'validTo'
              ? f('validToHint')
              : undefined
        }
        id={id}
        label={f(name)}
        name={name}
        required={name === 'operationStartDate' || name === 'validFrom'}
      />
    )
  }
  const selectOptions =
    name === 'generatorCategory'
      ? GENERATOR_CATEGORY_OPTIONS.map((value) => ({
          value: String(value),
          label: generatorCategoryLabel(t, value),
        }))
      : name === 'powerType'
        ? UNIT_POWER_TYPE_OPTIONS.map((value) => ({
            value: String(value),
            label: plantPowerTypeLabel(value),
          }))
        : name === 'plantMasterId'
          ? [
              { value: '', label: f('plantCodeNone') },
              ...plantCandidates(plants, {
                areaCode:
                  sites.find((site) => site.id === siteId)?.areaCode ??
                  AreaCode.UNSPECIFIED,
                currentPlantMasterId: plantMasterId,
              }).map((plant) => ({
                value: plant.id,
                label: `${plant.plantCode} ${plant.plantName}`,
              })),
            ]
          : undefined
  return (
    <FieldRoot alignItems="stretch" invalid={errors[name] !== undefined}>
      {name === 'siteId' ? (
        <SiteSelectField
          allowAdd={false}
          areaCode={AreaCode.UNSPECIFIED}
          control={control}
          currentSiteId={siteId}
          inputId={id}
          isDisabled={disabled}
          onSiteChanged={onSiteChanged}
          sites={sites}
        />
      ) : selectOptions !== undefined ? (
        <FormSelect
          aria-label={f(name === 'plantMasterId' ? 'plantCode' : name)}
          control={control}
          inputId={id}
          isDisabled={disabled}
          name={name}
          numeric={name === 'generatorCategory' || name === 'powerType'}
          options={selectOptions}
        />
      ) : name === 'note' ? (
        <NoteField
          control={control}
          disabled={disabled}
          errors={errors}
          hideLabel
          idPrefix="detail-unit"
        />
      ) : name === 'unitName' ||
        name === 'unitNameAbbr' ||
        name === 'authorizedOutputKw' ||
        name === 'receivingPointId' ||
        name === 'powerSourceId' ||
        name === 'generatorIdentifier' ? (
        <TextField
          control={control}
          disabled={disabled}
          errors={errors}
          hideLabel
          hint={
            name === 'authorizedOutputKw'
              ? f('authorizedOutputHint')
              : name === 'unitName'
                ? undefined
                : f(`${name}Hint`)
          }
          id={id}
          inputMode={
            name === 'authorizedOutputKw' || name === 'receivingPointId'
              ? 'numeric'
              : undefined
          }
          label={f(name)}
          max={MAX_LENGTHS[name]}
          name={name}
          required={
            name === 'unitName' ||
            name === 'unitNameAbbr' ||
            name === 'authorizedOutputKw'
          }
        />
      ) : null}
      {(name === 'siteId' || selectOptions !== undefined) &&
        errors[name] !== undefined && (
          <FieldErrorText>{errors[name]?.message}</FieldErrorText>
        )}
    </FieldRoot>
  )
}
